import React, { useEffect, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Images, MapPin, X } from 'lucide-react';
import { SortSelector } from '../ui/SortSelector';
import SpotCard from './SpotCard';
import { getProcessedSpots } from '../../lib/spotSorting';
import { MetadataFilterSummary } from '../ui/ClickableMetadataBadge';

function SpotList() {
  const { state, actions } = useApp();
  const navigate = useNavigate();

  // Process and sort spots based on current settings
  const processedSpots = useMemo(() => {
    const baseSpots = state.selectedSpotFromPin 
      ? [state.selectedSpotFromPin] 
      : state.filteredSpots.length > 0 
        ? state.filteredSpots 
        : state.spots;

    return getProcessedSpots(
      baseSpots,
      state.userLocation,
      state.sortMode,
      state.sortOrder,
      state.enableSecondarySorting ? state.secondarySortMode : null,
      state.secondarySortOrder
    );
  }, [
    state.spots, 
    state.filteredSpots, 
    state.selectedSpotFromPin,
    state.userLocation, 
    state.sortMode, 
    state.sortOrder,
    state.secondarySortMode,
    state.secondarySortOrder,
    state.enableSecondarySorting
  ]);

  const spotsToShow = processedSpots;

  // Filter spots based on search query and filters
  useEffect(() => {
    let filtered = [...state.spots];
    
    // Filtre par recherche texte
    if (state.searchQuery.trim()) {
      const query = state.searchQuery.toLowerCase();
      filtered = filtered.filter(spot => 
        spot.title.toLowerCase().includes(query) ||
        spot.description.toLowerCase().includes(query) ||
        spot.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }
    
    // Filtre par tags sélectionnés (AND logic - le spot doit avoir TOUS les tags)
    if (state.selectedTags.length > 0) {
      filtered = filtered.filter(spot =>
        state.selectedTags.every(tag => spot.tags?.includes(tag))
      );
    }
    
    // Filtre par métadonnées V2 (filtres essentiels)
    // Logique: OR au sein d'une catégorie, AND entre catégories
    // Ex: (Coucher de soleil OU Lever de soleil) ET (Ciel dégagé OU Partiellement nuageux)
    const { selectedMetadataFilters } = state;
    
    // Filtre par créneaux horaires (OR - le spot doit avoir AU MOINS UN des créneaux sélectionnés)
    if (selectedMetadataFilters.time_windows?.length > 0) {
      filtered = filtered.filter(spot => {
        const spotTimeWindows = spot.v2_data?.recommended_time_windows?.map(tw => tw.type) || [];
        // OR logic: au moins un des créneaux sélectionnés doit être présent
        return selectedMetadataFilters.time_windows.some(tw => spotTimeWindows.includes(tw));
      });
    }
    
    // Filtre par météo (OR - le spot doit avoir AU MOINS UNE des conditions météo sélectionnées)
    if (selectedMetadataFilters.weather?.length > 0) {
      filtered = filtered.filter(spot => {
        const spotWeather = spot.v2_data?.ideal_conditions?.weather || [];
        return selectedMetadataFilters.weather.some(w => spotWeather.includes(w));
      });
    }
    
    // Filtre par lumière (OR - le spot doit avoir AU MOINS UNE des conditions de lumière sélectionnées)
    if (selectedMetadataFilters.light?.length > 0) {
      filtered = filtered.filter(spot => {
        const spotLight = spot.v2_data?.ideal_conditions?.light || [];
        return selectedMetadataFilters.light.some(l => spotLight.includes(l));
      });
    }
    
    // Filtre par affluence (OR - le spot doit avoir AU MOINS UN des niveaux sélectionnés)
    if (selectedMetadataFilters.crowd_level?.length > 0) {
      filtered = filtered.filter(spot => 
        selectedMetadataFilters.crowd_level.includes(spot.v2_data?.crowd_level)
      );
    }
    
    // Filtre par difficulté (OR - le spot doit avoir AU MOINS UNE des difficultés sélectionnées)
    if (selectedMetadataFilters.difficulty?.length > 0) {
      filtered = filtered.filter(spot => 
        selectedMetadataFilters.difficulty.includes(spot.v2_data?.accessibility?.difficulty)
      );
    }
    
    actions.setFilteredSpots(filtered);
  }, [state.searchQuery, state.spots, state.selectedTags, state.selectedMetadataFilters, actions]);

  const handleGalleryClick = () => {
    actions.setShowTikTokGallery(true);
  };

  const handleSortModeChange = (newMode) => {
    actions.setSortMode(newMode);
  };

  const handleSortOrderChange = (newOrder) => {
    actions.setSortOrder(newOrder);
  };

  const handleSecondarySortModeChange = (newMode) => {
    actions.setSecondarySortMode(newMode);
  };

  const handleSecondarySortOrderChange = (newOrder) => {
    actions.setSecondarySortOrder(newOrder);
  };

  const handleEnableSecondarySortingChange = (enable) => {
    actions.setEnableSecondarySorting(enable);
  };

  // Generate dynamic sorting description
  const getSortingDescription = () => {
    const sortOptions = {
      'proximity': 'proximité',
      'date': 'date',
      'popularity': 'popularité',
      'title': 'nom'
    };

    let description = `Triés par ${sortOptions[state.sortMode] || 'défaut'}`;
    
    if (state.enableSecondarySorting && state.secondarySortMode && state.secondarySortMode !== state.sortMode) {
      description += ` puis ${sortOptions[state.secondarySortMode]}`;
    }

    return description;
  };

  // Check if geolocation is being requested or already available
  const hasGeolocationSupport = !!navigator.geolocation;
  const hasUserLocation = !!state.userLocation;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              {state.selectedSpotFromPin ? (
                <span>Spot sélectionné</span>
              ) : (
                <span>Spots disponibles</span>
              )}
            </h2>
            <p className="text-sm text-gray-600">
              {spotsToShow.length} spot{spotsToShow.length > 1 ? 's' : ''} trouvé{spotsToShow.length > 1 ? 's' : ''}
              <span className="ml-2 text-blue-600">
                📊 {getSortingDescription()}
              </span>
            </p>
          </div>
          
          {/* Galerie Button */}
          <Button
            variant="outline"
            onClick={handleGalleryClick}
            className="flex items-center space-x-2"
          >
            <Images size={16} />
            <span>Galerie</span>
          </Button>
        </div>

        {/* Sort Controls and Location Status */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <SortSelector
              sortMode={state.sortMode}
              sortOrder={state.sortOrder}
              secondarySortMode={state.secondarySortMode}
              secondarySortOrder={state.secondarySortOrder}
              enableSecondarySorting={state.enableSecondarySorting}
              onSortModeChange={handleSortModeChange}
              onSortOrderChange={handleSortOrderChange}
              onSecondarySortModeChange={handleSecondarySortModeChange}
              onSecondarySortOrderChange={handleSecondarySortOrderChange}
              onEnableSecondarySortingChange={handleEnableSecondarySortingChange}
              hasUserLocation={hasUserLocation}
              disabled={state.loading}
            />
            
            {/* Location Status */}
            {!hasUserLocation && hasGeolocationSupport && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  // Request geolocation permission
                  if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition(
                      (position) => {
                        const { latitude, longitude, accuracy } = position.coords;
                        actions.setUserLocation([latitude, longitude]);
                        actions.setUserLocationAccuracy(accuracy);
                        // Auto-switch to proximity sorting when location is obtained
                        if (state.sortMode !== 'proximity') {
                          actions.setSortMode('proximity');
                          actions.setSortOrder('asc');
                        }
                      },
                      (error) => {
                        console.warn('Géolocalisation refusée:', error);
                        // Don't switch to proximity mode if location fails
                      },
                      {
                        enableHighAccuracy: true,
                        timeout: 10000,
                        maximumAge: 300000 // 5 minutes cache
                      }
                    );
                  }
                }}
                className="flex items-center space-x-2 text-xs text-blue-600 border-blue-200"
              >
                <MapPin size={14} />
                <span>Activer localisation</span>
              </Button>
            )}
            
            {!hasGeolocationSupport && (
              <span className="text-xs text-gray-500">
                Géolocalisation non supportée
              </span>
            )}
          </div>
        </div>

        {/* Clear filter button when spot is selected from pin */}
        {state.selectedSpotFromPin && (
          <Button
            variant="outline"
            onClick={() => actions.setSelectedSpotFromPin(null)}
            className="w-full mt-2"
          >
            Voir tous les spots
          </Button>
        )}

        {/* Active metadata filters summary */}
        <div className="mt-2">
          <MetadataFilterSummary />
        </div>
      </div>

      {/* Spots List */}
      <div className="flex-1 overflow-y-auto p-4">
        {spotsToShow.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p className="text-lg font-medium">Aucun spot trouvé</p>
            
            {/* Message d'aide si des filtres sont actifs */}
            {(state.selectedTags?.length > 0 || 
              Object.values(state.selectedMetadataFilters).flat().length > 0) && (
              <div className="mt-3 p-3 bg-amber-50 rounded-lg text-amber-700 text-sm">
                <p className="font-medium">💡 Trop de filtres ?</p>
                <p className="mt-1">Essayez de réduire le nombre de filtres pour trouver des spots.</p>
              </div>
            )}
            
            {state.searchQuery && (
              <Button
                variant="ghost"
                onClick={() => actions.setSearchQuery('')}
                className="mt-2"
              >
                Effacer la recherche
              </Button>
            )}
            
            {(state.selectedTags?.length > 0 || 
              Object.values(state.selectedMetadataFilters).flat().length > 0) && (
              <Button
                variant="outline"
                onClick={() => {
                  actions.setSelectedTags([]);
                  actions.clearMetadataFilters();
                }}
                className="mt-2 text-red-500 border-red-200 hover:bg-red-50"
              >
                Effacer tous les filtres
              </Button>
            )}
          </div>
        ) : (
          <div className={
            // Mode grille automatique si la section spots fait plus de 50% de largeur
            (100 - state.mapWidth) > 50 
              ? "grid grid-cols-3 gap-3" 
              : "space-y-4"
          }>
            {spotsToShow.map((spot, index) => (
              <SpotCard
                key={spot.id}
                spot={spot}
                index={index}
                isSelected={state.selectedSpot?.id === spot.id}
                displayMode={(100 - state.mapWidth) > 50 ? 'grid' : 'list'}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SpotList;